import { Component, Input, OnInit } from '@angular/core';
import { ModalController, ToastController } from '@ionic/angular';
import { ArkiusShareModalComponent } from '../arkius-share-modal/arkius-share-modal.component';
import { ActionService, ShareService } from '@arkius/shared-api';
import { firstValueFrom, take } from 'rxjs';
import { InAppBrowser } from '@capacitor/inappbrowser';
import { Capacitor } from '@capacitor/core';
import { environment } from '@shared-envs';

@Component({
  selector: 'ark-lib-share-modal',
  templateUrl: './share-modal.component.html',
  styleUrls: ['./share-modal.component.css'],
})
export class ShareModalComponent implements OnInit {
  @Input() id?: number;
  @Input() caseUrl?: URL;
  @Input() socialOnly = false;
  @Input() directShareUrl?: string;

  private _type: 'proposition' | 'comment' | 'commentReply' | 'idea' | 'member' | 'problem' =
    'proposition';

  @Input()
  set type(
    value:
      | 'proposition'
      | 'comment'
      | 'commentReply'
      | 'idea'
      | 'thinkTank'
      | 'member'
      | 'problem',
  ) {
    if (value === 'thinkTank') {
      this._type = 'proposition';
    } else {
      this._type = value;
    }
  }

  get type(): 'proposition' | 'comment' | 'commentReply' | 'idea' | 'member' | 'problem' {
    return this._type;
  }

  selectedIcon: string | null = null;
  caseShareUrl: string | null = null;
  linkCopied = false;
  buttonState: 'secondary' | 'active' | 'confirmed' = 'secondary';
  copyIconState: 'copy-default' | 'copy-selected' = 'copy-default';

  constructor(
    private modalCtrl: ModalController,
    private actionService: ActionService,
    private shareService: ShareService,
    private toastCtrl: ToastController,
  ) {}

  ngOnInit() {
    if (this.directShareUrl) {
      this.caseShareUrl = this.directShareUrl;
      return;
    }

    this.generateSharableLink();

    console.log('ShareModalComponent initialized with id:', this.id, 'and type:', this.type);
  }

  async selectIcon(iconName: string) {
    if (this.selectedIcon === iconName) {
      // Deselect if clicked again
      this.selectedIcon = null;
      return;
    }

    this.selectedIcon = iconName;

    if (iconName !== 'Copy') {
      this.linkCopied = false;
    } else {
      // Reset Copy icon to normal state when re-selected
      this.copyIconState = 'copy-selected';
    }
  }

  async generateSharableLink() {
    if (this.id == null || !this.caseUrl) return;

    const share = await firstValueFrom(
      this.actionService.apiV5ActionSharableLinkPost({
        EntityId: this.id,
        EntityType: this.type,
      }),
    );

    this.caseUrl.searchParams.set('link', share.link);
    this.caseShareUrl = this.caseUrl.toString();
  }

  async share() {
    const url = this.caseShareUrl!;

    if (this.selectedIcon === 'The Solutions Movement') {
      await this.presentFullScreenModal();
      return;
    }

    if (!this.selectedIcon) {
      console.warn('No sharing platform selected.');
      return;
    }

    const shareData =
      this.id != null
        ? {
            EntityType: this.type,
            EntityId: this.id,
            SocialName: this.selectedIcon,
          }
        : null;

    let shareUrl = '';
    switch (this.selectedIcon) {
      case 'X':
        shareUrl = `https://x.com/intent/post?text=${encodeURIComponent(url)}`;
        break;
      case 'Facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'LinkedIn':
        shareUrl = `https://www.linkedin.com/feed/?shareActive=true&shareUrl=${encodeURIComponent(url)}`;
        break;
      default:
        console.warn('Invalid sharing platform selected.');
        return;
    }

    if (Capacitor.getPlatform() === 'web') {
      window.open(shareUrl, '_blank');
    } else {
      await InAppBrowser.openInExternalBrowser({ url: shareUrl });
    }

    if (shareData) {
      this.shareService
        .apiV5SharePost(shareData)
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            environment.log('Share action recorded:', response);
            this.modalCtrl.dismiss({ shared: true }, 'confirm');
          },
        });
    }
  }

  async presentFullScreenModal() {
    const modal = await this.modalCtrl.create({
      component: ArkiusShareModalComponent,
      initialBreakpoint: 1,
      breakpoints: [0, 1],
      componentProps: {
        id: this.id,
        type: this.type,
        socialName: this.selectedIcon,
      },
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();
    if (role === 'confirm') {
      environment.log('Full-screen modal confirmed with data:', data);
      await this.modalCtrl.dismiss({ shared: true }, 'confirm');
    }
  }

  cancel() {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  copyToClipboard() {
    environment.log('Copying to clipboard:', this.caseShareUrl);
    if (!this.caseShareUrl) return;

    navigator.clipboard.writeText(this.caseShareUrl).then(
      () => {
        this.linkCopied = true;
        this.buttonState = 'active';

        setTimeout(() => {
          this.buttonState = 'confirmed';
          this.copyIconState = 'copy-default';
          environment.log(
            'Copy icon state reset to copy-active',
            this.copyIconState,
          );
        }, 2000);
      },
      (err) => console.error('Could not copy text: ', err),
    );
  }

  getIconName(icon: string): string {
    const isSelected = this.selectedIcon === icon;

    // Before anything is selected
    if (!this.selectedIcon) {
      switch (icon) {
        case 'X':
          return 'twitter-default';
        case 'Facebook':
          return 'facebook-default';
        case 'LinkedIn':
          return 'linkedin-default';
        case 'The Solutions Movement':
          return 'arkius-default';
        case 'Copy':
          return 'copy-active';
        default:
          return `${icon}-default`;
      }
    }

    // After selection
    switch (icon) {
      case 'Copy':
        return isSelected ? this.copyIconState : 'copy-inactive';
      case 'X':
        return isSelected ? 'twitter' : 'twitter-inactive';
      case 'Facebook':
        return isSelected ? 'facebook' : 'facebook-inactive';
      case 'LinkedIn':
        return isSelected ? 'linkedin' : 'linkedin-inactive';
      case 'The Solutions Movement':
        return isSelected ? 'logo-share' : 'arkius-inactive';
      default:
        return `${icon}-inactive`;
    }
  }
}
